/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import CSSTransition from 'react-transition-group/CSSTransition';
import React, { Component, ReactElement } from 'react';
import ReactDOM from 'react-dom';
import { Toast } from '@react/react-spectrum/Toast';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import ChevronRight from '@react/react-spectrum/Icon/ChevronRight';

/* eslint-enable no-unused-vars */
import './ErrorToast.scss';

let TOAST_CONTAINER: ErrorToastContainer;
const TOAST_TIMEOUT = 5000;
const TOAST_ANIMATION_TIME = 200;

type State = {
	toasts: Array<genericObject>;
};

type Action = {
	message: string;
	className: string;
	callback: Function;
};

export class ErrorToastContainer extends Component<null, State> {
	state = {
		toasts: [],
	};

	/**
	 * Add a toast to the array of toasts
	 *
	 * @param {Object} toast - toast to add
	 * @param {number} [timeout=TOAST_TIMEOUT] - timer to remove the toast, default to TOAST_TIMEOUT
	 * @memberof ErrorToast
	 */
	add(toast: genericObject, timeout: number = TOAST_TIMEOUT) {
		this.setState({
			toasts: [...this.state.toasts, toast],
		});

		/* istanbul ignore else */
		if (timeout) {
			setTimeout(this.remove.bind(this, toast), timeout);
		}
	}

	/**
	 * Remove a toast from the array of toast
	 *
	 * @param {Object} toast - toast to remove
	 * @memberof ErrorToast
	 */
	remove(toast: genericObject) {
		this.setState({
			toasts: this.state.toasts.filter((t) => t !== toast),
		});
	}

	render() {
		return (
			<TransitionGroup className="react-spectrum-ToastContainer error-Toast-Container">
				{this.state.toasts.map((toast: ReactElement, i) => (
					<CSSTransition key={i} classNames="react-spectrum-Toast-slide" timeout={TOAST_ANIMATION_TIME}>
						{React.cloneElement(toast, { onClose: this.remove.bind(this, toast) })}
					</CSSTransition>
				))}
			</TransitionGroup>
		);
	}
}

/**
 * Getter of the Toast Container
 * This will create a new Toast Container and add it to DOM if not exsited
 */
function ensureToastContainer(): Promise<ErrorToastContainer> {
	if (TOAST_CONTAINER) {
		return Promise.resolve(TOAST_CONTAINER);
	}

	return new Promise((resolve) => {
		const node = document.createElement('div');

		/* istanbul ignore else */
		if (document.body) {
			document.body.appendChild(node);
			// @ts-ignore
			ReactDOM.render(<ErrorToastContainer ref={(ref) => (TOAST_CONTAINER = ref)} />, node, () => {
				resolve(TOAST_CONTAINER);
			});
		}
	});
}

/**
 * Add a toast to Toast Container
 *
 * @export
 * @param {Object} toast - toast to add
 * @param {number} timeout - timer to remove the toast
 */
export function addToast(toast: Object, timeout: number) {
	ensureToastContainer().then((ref) => ref.add(toast, timeout));
}

// /**
//  * Remove a toast from Toast Container
//  *
//  * @export
//  * @param {Object} toast - toast to remove
//  */
// export function removeToast(toast: Object) {
// 	ensureToastContainer().then(ref => ref.remove(toast));
// }

/**
 * Throw an Error Toast
 *
 * @export
 * @param {string} message - Description of the Error
 * @param {Object} [options={}] - optional props to customize Toast from React Spectrum
 */
export function error(message: string, options: genericObject = {}) {
	/* istanbul ignore else */
	if (!(options.msgId && document.getElementsByClassName(options.msgId).length > 0)) {
		addToast(
			<Toast closable className={`closeable-error ${options.msgId}`} variant="error" {...options}>
				{message}
			</Toast>,
			options.timeout,
		);
	}
}

/**
 * Throw a Customizable Error Toast
 * Example: errorWithAction('There was a problem installing Photoshop CC', {message: 'Learn more', className: 'learn-more', callback: () => console.log('calling customizableError')}, {timeout: 8000});
 * @export
 * @param {string} message - Description of the Error
 * @param {Object} [options={}] - optional props to customize Toast from React Spectrum
 * @param {Action} action - Action object
 */
export function errorWithAction(message: string, action: genericObject, options: genericObject = {}) {
	addToast(
		<Toast variant="error" className={`customizable-Error-Toast ${options.className}`} {...options}>
			<div className={`error-Toast-Message ${action.className}`}>
				{message}
				<span className="error-Toast-Action" onClick={action.callback}>
					{action.message}
				</span>
			</div>
			<div className={`error-Toast-Message-small ${action.className}`} onClick={action.callback}>
				{message}
				<span className="error-Toast-Action-small">
					<ChevronRight size="S" />
				</span>
			</div>
		</Toast>,
		options.timeout,
	);
}
